import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Squad, User } from '../../../types';
import { useAuth } from '../../auth/contexts/AuthContext';
import { collection, doc, getDoc, onSnapshot, query, where, documentId } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { FirebaseService } from '../../../shared/storage/FirebaseService';
import TeacherToolbelt from './TeacherToolbelt';
import RewardModal from './RewardModal';
import RewardConfirmationModal from './RewardConfirmationModal';
import SquadGameAccessModal from './SquadGameAccessModal';
import SquadBoardModeModal from './SquadBoardModeModal';
import DraggableTimer from '../../../shared/ui/DraggableTimer';
import DraggableMusic from '../../../shared/ui/DraggableMusic';
import MissionTransportProgress from './MissionTransportProgress';
import MissionAchievementLiveFeed, { MissionAchievementFeedItem } from './MissionAchievementLiveFeed';
import { ActivityModal } from '../../curriculum/components/ActivityModal';
import { useCurriculumAdapter } from '../../curriculum/hooks/useCurriculumAdapter';
import { getSquadCurriculumId } from '../utils/curriculum';
import { getGameById } from '../../games/models/GameRegistry';
import { gameAPI } from '../../games/services/GameAPI';
import { playSound } from '../../../utils/audio';
import BonusActivitiesModal from './BonusActivitiesModal';
import '../styles/MissionDashboard.css';
import { canAccessSquad } from '../utils/squadAccess';

interface LocationState {
  activity?: any;
  levelId?: string;
}

const MissionDashboard: React.FC = () => {
  const { squadId, levelId: levelIdParam, missionId } = useParams<{
    squadId: string;
    levelId: string;
    missionId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userData } = useAuth();

  const locationState = (location.state || {}) as LocationState;

  const [squad, setSquad] = useState<Squad | null>(null);
  const [students, setStudents] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [isRewardConfirmationOpen, setIsRewardConfirmationOpen] = useState(false);
  const [pendingReward, setPendingReward] = useState<{
    students: { id: string; name: string; avatar: string; currentMusecash: number }[];
    amount: number;
  } | null>(null);
  const [isAwardingReward, setIsAwardingReward] = useState(false);

  const [isTimerOpen, setIsTimerOpen] = useState(false);
  const [isGameAccessOpen, setIsGameAccessOpen] = useState(false);
  const [isMusicOpen, setIsMusicOpen] = useState(false);
  const [isBoardModeOpen, setIsBoardModeOpen] = useState(false);
  const [isBonusActivitiesOpen, setIsBonusActivitiesOpen] = useState(false);

  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [activity, setActivity] = useState<any | null>(locationState.activity || null);
  const [expandedGoalIds, setExpandedGoalIds] = useState<string[]>([]);
  const [dailyPracticeCompletedByStudentId, setDailyPracticeCompletedByStudentId] = useState<Record<string, boolean>>({});

  const levelId = levelIdParam || locationState.levelId || '';

  const [achievementFeedItems, setAchievementFeedItems] = useState<MissionAchievementFeedItem[]>([]);
  const [museCashCelebrationByStudentId, setMuseCashCelebrationByStudentId] = useState<Record<string, number>>({});
  const celebrationTimeoutsRef = useRef<Record<string, number>>({});

  // Load squad
  useEffect(() => {
    if (!squadId || !currentUser) {
      setError('Squad not found or user not logged in.');
      setLoading(false);
      return;
    }

    setLoading(true);
    const squadRef = doc(db, 'squads', squadId);

    const unsubscribe = onSnapshot(
      squadRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const squadData = { ...docSnap.data(), id: docSnap.id } as Squad;
          if (!canAccessSquad(userData, squadData)) {
            setError("You don't have permission to view this squad.");
            setSquad(null);
          } else {
            setSquad(squadData);
            setError(null);
          }
        } else {
          setError('Squad not found.');
          setSquad(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching squad with snapshot: ', err);
        setError('Failed to load squad data.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, squadId, userData]);

  // Load students for reward / board mode / game access
  useEffect(() => {
    if (!squad?.studentIds || squad.studentIds.length === 0) {
      setStudents([]);
      return;
    }
    const studentsRef = collection(db, 'users');
    const studentsQuery = query(studentsRef, where(documentId(), 'in', squad.studentIds));
    const unsubscribe = onSnapshot(studentsQuery, (studentsSnapshot) => {
      const studentsData = studentsSnapshot.docs.map(
        (docSnap) => ({ ...docSnap.data(), id: docSnap.id } as User)
      );
      setStudents(studentsData);
    });
    return () => unsubscribe();
  }, [squad?.studentIds]);

  // Fallback: if activity wasn't passed via navigation state, try to resolve from curriculum
  const adapter = useCurriculumAdapter({ squad: squad || undefined });

  const selectedLevel = useMemo(() => {
    if (!squad || !levelId) return null;
    try {
      return adapter.getLevelById(levelId) as any;
    } catch {
      return null;
    }
  }, [adapter, levelId, squad]);

  useEffect(() => {
    if (!activity && missionId) {
      try {
        const resolved = adapter.getMissionActivity(missionId);
        if (resolved) {
          setActivity(resolved);
        }
      } catch (e) {
        console.warn('Unable to resolve activity from adapter', e);
      }
    }
  }, [activity, missionId, adapter]);

  const handleRewardConfirm = async (
    selectedStudentData: { id: string; name: string; avatar: string }[],
    amount: number
  ) => {
    try {
      const studentsWithMusecash = await Promise.all(
        selectedStudentData.map(async (student) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', student.id));
            const userData = userDoc.data();
            const currentMusecash = userData?.museCash || 0;
            return {
              ...student,
              currentMusecash,
            };
          } catch (error) {
            console.error(`Failed to fetch musecash for ${student.name}:`, error);
            return {
              ...student,
              currentMusecash: 0,
            };
          }
        })
      );

      setPendingReward({ students: studentsWithMusecash, amount });
      setIsRewardModalOpen(false);
      setIsRewardConfirmationOpen(true);
    } catch (error) {
      console.error('Error preparing reward confirmation:', error);
      void window.appAlert('An error occurred while preparing the reward. Please try again.');
    }
  };

  const handleConfirmReward = async () => {
    if (!pendingReward) return;

    setIsAwardingReward(true);

    try {
      const rewardResults = pendingReward.students.map(async (student) => {
        try {
          const newMuseCashAmount = student.currentMusecash + pendingReward.amount;
          await FirebaseService.updateMuseCash(student.id, newMuseCashAmount);
          console.log(
            `Awarded ${pendingReward.amount} musecash to ${student.name} (${student.currentMusecash} → ${newMuseCashAmount})`
          );
          return { success: true, student: student.name };
        } catch (error) {
          console.error(`Failed to award musecash to ${student.name}:`, error);
          return { success: false, student: student.name, error };
        }
      });

      await Promise.all(rewardResults);
    } catch (error) {
      console.error('Error awarding musecash:', error);
      void window.appAlert('An error occurred while awarding Musecash. Please try again.');
    } finally {
      setIsAwardingReward(false);
      setIsRewardConfirmationOpen(false);
      setPendingReward(null);
    }
  };

  const handleBackToSquad = () => {
    if (squadId) {
      navigate(`/app/squads/${squadId}`);
    } else {
      navigate('/app/squads');
    }
  };

  const handleReportBug = () => {
    window.open('https://tally.so/r/31a5MO', '_blank', 'noopener,noreferrer');
  };

  const handleOpenGame = () => {
    if (missionGameIdToOpen) {
      window.open(`/play/${missionGameIdToOpen}`, '_blank', 'noopener,noreferrer');
    }
  };

  const curriculumId = useMemo(() => {
    return squad ? getSquadCurriculumId(squad) || 'music' : 'music';
  }, [squad]);

  const goals = useMemo(() => {
    if (!activity) return [];
    const anyActivity = activity as any;
    const source =
      Array.isArray(anyActivity.goals) && anyActivity.goals.length > 0
        ? anyActivity.goals
        : Array.isArray(anyActivity.tasks)
        ? anyActivity.tasks
        : [];
    return source.filter((g: any) => g && (typeof g.text === 'string' || typeof g.shortText === 'string'));
  }, [activity]);

  const getGoalIcon = (goal: any) => {
    if (((goal?.type as string) || '').toLowerCase() === 'dailyachievement') {
      return '🏆';
    }
    return '✋';
  };

  const missionGameId = useMemo(() => {
    if (!selectedLevel || !missionId) return undefined;
    const missions: any[] = (selectedLevel as any).missions || [];
    const mission = missions.find(m => m && m.id === missionId);
    return mission && (mission as any).gameId ? String((mission as any).gameId) : undefined;
  }, [selectedLevel, missionId]);

  const missionGameIdToOpen = useMemo(() => {
    if (!missionGameId) return undefined;
    // Some games have separate student/teacher variants (e.g. React Attack).
    // When the teacher clicks "Open Game" on the mission dashboard, prefer the teacher variant if it exists.
    if (missionGameId.endsWith('-student')) {
      const teacherVariant = missionGameId.replace(/-student$/, '-teacher');
      if (getGameById(teacherVariant)) return teacherVariant;
    }
    return missionGameId;
  }, [missionGameId]);

  // For boat riders: check each student's Firestore achievements for today's Daily Practice (daily-play) for this mission's game
  useEffect(() => {
    if (!squad || !missionGameId || students.length === 0) {
      setDailyPracticeCompletedByStudentId({});
      return;
    }

    // If attendance has any "present" marks, only include present students; otherwise include all students
    const attendanceEntries = Object.entries(squad.attendance || {});
    const hasAnyPresent = attendanceEntries.some(([_, present]) => !!present);
    const eligibleIds = hasAnyPresent
      ? attendanceEntries.filter(([_, present]) => !!present).map(([id]) => id)
      : students.map(s => s.id);

    const today = new Date();
    const isSameLocalDay = (d: Date) =>
      d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear();

    const unsubscribers: Array<() => void> = [];

    // Reset to false for all eligible IDs initially
    setDailyPracticeCompletedByStudentId(prev => {
      const next: Record<string, boolean> = { ...prev };
      eligibleIds.forEach(id => { next[id] = false; });
      return next;
    });

    eligibleIds.forEach((studentId) => {
      try {
        const achievementsRef = collection(db, 'users', studentId, 'achievements');
        const unsub = onSnapshot(
          achievementsRef,
          { includeMetadataChanges: false },
          (snap) => {
            let completedToday = false;
            snap.docs.forEach(docSnap => {
              const a: any = docSnap.data();
              const gameId = a?.gameId;
              const achievementId = a?.achievementId;
              if (String(gameId) !== String(missionGameId)) return;
              if (String(achievementId) !== 'daily-play') return;

              const unlockedAtRaw = a?.unlockedAt;
              let unlockedAt: Date | null = null;
              if (unlockedAtRaw instanceof Date) {
                unlockedAt = unlockedAtRaw;
              } else if (unlockedAtRaw && typeof unlockedAtRaw.toDate === 'function') {
                // Firestore Timestamp
                unlockedAt = unlockedAtRaw.toDate();
              } else if (typeof unlockedAtRaw === 'string' || typeof unlockedAtRaw === 'number') {
                const parsed = new Date(unlockedAtRaw);
                if (!isNaN(parsed.getTime())) unlockedAt = parsed;
              }

              if (unlockedAt && isSameLocalDay(unlockedAt)) {
                completedToday = true;
              }
            });

            setDailyPracticeCompletedByStudentId(prev => {
              if (prev[studentId] === completedToday) return prev;
              return { ...prev, [studentId]: completedToday };
            });
          },
          (err) => {
            console.warn('Daily practice listener error for user', studentId, err);
          }
        );
        unsubscribers.push(unsub);
      } catch (e) {
        console.warn('Failed to set up daily practice listener for user', studentId, e);
      }
    });

    return () => {
      unsubscribers.forEach(u => u());
    };
  }, [missionGameId, squad, students]);

  const boatMuses = useMemo(() => {
    if (!squad || students.length === 0) return [];
    return students
      .filter(student => !!dailyPracticeCompletedByStudentId[student.id])
      .map(student => ({
        id: student.id,
        name: student.displayName || 'Student',
        img:
          (student.selectedMuse &&
            typeof student.selectedMuse === 'object' &&
            (student.selectedMuse as any).img) ||
          undefined,
      }));
  }, [dailyPracticeCompletedByStudentId, squad, students]);

  const gameName = useMemo(() => {
    if (!missionGameId) return undefined;
    const game = getGameById(missionGameId);
    return game?.title;
  }, [missionGameId]);

  const attendanceCount = useMemo(() => {
    if (!squad) return 0;
    const attendanceEntries = Object.entries(squad.attendance || {});
    const hasAnyPresent = attendanceEntries.some(([_, present]) => !!present);
    if (hasAnyPresent) {
      return attendanceEntries.filter(([_, present]) => !!present).length;
    }
    return students.length;
  }, [squad, students]);

  const attendanceStudentIds = useMemo(() => {
    if (!squad) return [];
    const attendanceEntries = Object.entries(squad.attendance || {});
    const hasAnyPresent = attendanceEntries.some(([_, present]) => !!present);
    if (hasAnyPresent) {
      return attendanceEntries.filter(([_, present]) => !!present).map(([id]) => id);
    }
    return students.map(s => s.id);
  }, [squad, students]);

  const attendanceStudents = useMemo(() => {
    if (attendanceStudentIds.length === 0) return [];
    const idSet = new Set(attendanceStudentIds);
    return students.filter(s => idSet.has(s.id));
  }, [attendanceStudentIds, students]);

  const triggerMuseCashCelebration = (studentId: string) => {
    setMuseCashCelebrationByStudentId(prev => {
      const nextKey = Date.now();
      return { ...prev, [studentId]: nextKey };
    });

    const existing = celebrationTimeoutsRef.current[studentId];
    if (existing) window.clearTimeout(existing);
    celebrationTimeoutsRef.current[studentId] = window.setTimeout(() => {
      setMuseCashCelebrationByStudentId(prev => {
        if (!(studentId in prev)) return prev;
        const { [studentId]: _removed, ...rest } = prev;
        return rest;
      });
    }, 1200);
  };

  // Live achievements feed (attendance-only), real-time from users/{id}/achievements
  useEffect(() => {
    if (!squad || attendanceStudentIds.length === 0) {
      setAchievementFeedItems([]);
      return;
    }

    const mountedAtMs = Date.now();
    const seenByUserDocId = new Map<string, number>(); // `${studentId}:${docId}` -> unlockedAtMs
    const initializedUsers = new Set<string>();
    const unsubscribers: Array<() => void> = [];

    const toDate = (value: any): Date | null => {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value?.toDate === 'function') return value.toDate();
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    };

    const safeGetStudent = (studentId: string) =>
      students.find(s => s.id === studentId) || null;

    attendanceStudentIds.forEach((studentId) => {
      try {
        const achievementsRef = collection(db, 'users', studentId, 'achievements');
        const unsub = onSnapshot(
          achievementsRef,
          { includeMetadataChanges: false },
          (snap) => {
            if (!initializedUsers.has(studentId)) {
              snap.docs.forEach(docSnap => {
                const data: any = docSnap.data();
                const unlockedAt = toDate(data?.unlockedAt);
                const ms = unlockedAt?.getTime() || 0;
                seenByUserDocId.set(`${studentId}:${docSnap.id}`, ms);
              });
              initializedUsers.add(studentId);
              return;
            }

            const changes = snap.docChanges();
            changes.forEach((change) => {
              if (change.type !== 'added' && change.type !== 'modified') return;
              const data: any = change.doc.data();
              const unlockedAt = toDate(data?.unlockedAt);
              const unlockedAtMs = unlockedAt?.getTime() || 0;
              const seenKey = `${studentId}:${change.doc.id}`;
              const prevMs = seenByUserDocId.get(seenKey);

              // Only act on true timestamp changes
              if (prevMs === unlockedAtMs) return;
              seenByUserDocId.set(seenKey, unlockedAtMs);

              // Ignore pre-existing achievements (initial history / older unlocks)
              if (!unlockedAtMs || unlockedAtMs < mountedAtMs - 1500) return;

              const gameId = String(data?.gameId || '');
              const achievementId = String(data?.achievementId || '');
              if (!gameId || !achievementId) return;

              const student = safeGetStudent(studentId);
              if (!student) return;

              void (async () => {
                let title = achievementId;
                let iconUrl = '🏆';
                let museCashReward = 0;
                let rarity: string | undefined = 'common';

                try {
                  const def = await gameAPI.getAchievementDefinition(gameId, achievementId);
                  if (def) {
                    title = def.title || title;
                    iconUrl = def.iconUrl || iconUrl;
                    museCashReward = Number(def.museCashReward || 0);
                    rarity = def.rarity || rarity;
                  }
                } catch {
                  // Definition lookup is best-effort
                }

                const museImg =
                  (student.selectedMuse &&
                    typeof student.selectedMuse === 'object' &&
                    (student.selectedMuse as any).img) ||
                  undefined;

                const item: MissionAchievementFeedItem = {
                  id: `${studentId}:${gameId}:${achievementId}:${unlockedAtMs}`,
                  studentId,
                  studentName: student.displayName || 'Student',
                  studentMuseImg: museImg,
                  achievementTitle: title,
                  achievementIcon: iconUrl,
                  museCashReward,
                  rarity,
                  unlockedAt: unlockedAt || undefined,
                };

                setAchievementFeedItems(prev => {
                  // De-dupe by id (can happen on rapid re-renders)
                  if (prev.some(x => x.id === item.id)) return prev;
                  return [item, ...prev].slice(0, 50);
                });

                if (museCashReward > 0) {
                  // Tasteful, short cash sound
                  try {
                    playSound('/audio/badge-drop.mp3', 0.35);
                  } catch {
                    // ignore audio failures (autoplay restrictions)
                  }
                  triggerMuseCashCelebration(studentId);
                }
              })();
            });
          },
          (err) => {
            console.warn('Mission achievement feed listener error for user', studentId, err);
          }
        );
        unsubscribers.push(unsub);
      } catch (e) {
        console.warn('Failed to set up mission achievement feed listener for user', studentId, e);
      }
    });

    return () => {
      unsubscribers.forEach(u => u());
    };
    // Intentionally depend on attendanceStudentIds and students list (names/muse images)
  }, [attendanceStudentIds, squad, students]);

  const completedDailyPracticeCount = useMemo(() => {
    if (!squad) return 0;
    const attendanceEntries = Object.entries(squad.attendance || {});
    const hasAnyPresent = attendanceEntries.some(([_, present]) => !!present);
    const eligibleIds = hasAnyPresent
      ? attendanceEntries.filter(([_, present]) => !!present).map(([id]) => id)
      : students.map(s => s.id);
    return eligibleIds.filter(id => !!dailyPracticeCompletedByStudentId[id]).length;
  }, [squad, students, dailyPracticeCompletedByStudentId]);

  const toggleGoalExpanded = (goalKey: string) => {
    setExpandedGoalIds(prev =>
      prev.includes(goalKey) ? prev.filter(id => id !== goalKey) : [...prev, goalKey]
    );
  };

  if (loading) {
    return <div className="mission-dashboard-page">Loading mission dashboard...</div>;
  }

  if (error) {
    return <div className="mission-dashboard-page">Error: {error}</div>;
  }

  if (!squad) {
    return <div className="mission-dashboard-page">Squad not found.</div>;
  }

  return (
    <div className="mission-dashboard-page">
      <header className="mission-dashboard-header">
        <button
          type="button"
          className="mission-header-button mission-back-button"
          onClick={handleBackToSquad}
          aria-label="Back to squad dashboard"
        >
          ←
        </button>
        <div className="mission-header-title-wrapper">
          <h1 className="mission-title">{activity?.title || 'Mission Dashboard'}</h1>
        </div>
        <button
          type="button"
          className="mission-header-button mission-done-button"
          onClick={handleBackToSquad}
        >
          Done
        </button>
      </header>

      <main className="mission-dashboard-body">
        <div className="mission-action-buttons">
          <button
            type="button"
            className="mission-instructions-button"
            onClick={() => setShowInstructionsModal(true)}
          >
            Instructions
          </button>
          <button
            type="button"
            className="mission-open-game-button"
            onClick={handleOpenGame}
            disabled={!missionGameIdToOpen}
          >
            Open Game
          </button>
        </div>
        <MissionTransportProgress 
          boatMuses={boatMuses}
          gameName={gameName}
          completedCount={completedDailyPracticeCount}
          totalCount={attendanceCount}
          museCashCelebrationByStudentId={museCashCelebrationByStudentId}
        />
      </main>

      {goals.length > 0 && (
        <div className="mission-goals-panel">
          <div className="mission-goals-title">Goals to Complete</div>
          <ul className="goals-list">
            {goals.map((goal: any, index: number) => {
              const key: string = (goal.id as string) || `goal-${index}`;
              const label = goal.shortText || goal.text;
              const isExpanded = expandedGoalIds.includes(key);
              return (
                <li key={key} className={`goal-item ${isExpanded ? 'expanded' : ''}`}>
                  <button
                    type="button"
                    className="goal-header"
                    onClick={() => toggleGoalExpanded(key)}
                  >
                    <span className="goal-icon">{getGoalIcon(goal)}</span>
                    <span className="goal-text">{label}</span>
                    <span className="goal-chevron">{isExpanded ? '▾' : '▸'}</span>
                  </button>
                  {isExpanded && goal.text && (
                    <div className="goal-long-text">
                      {goal.text}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <MissionAchievementLiveFeed
        items={achievementFeedItems}
        studentsInAttendance={attendanceStudents}
      />

      <TeacherToolbelt
        onRewardClick={() => setIsRewardModalOpen(true)}
        onManageGamesClick={() => setIsGameAccessOpen(true)}
        onReportBugClick={handleReportBug}
        onTimerClick={() => setIsTimerOpen(true)}
        onBonusActivitiesClick={() => setIsBonusActivitiesOpen(true)}
        onOGCreateClick={undefined}
        onMusicClick={() => setIsMusicOpen(true)}
        onBoardModeClick={() => setIsBoardModeOpen(true)}
      />

      <RewardModal
        isOpen={isRewardModalOpen}
        onClose={() => setIsRewardModalOpen(false)}
        students={students.map((student) => ({
          id: student.id,
          name: student.displayName || 'Student',
          avatar:
            (student.selectedMuse &&
              typeof student.selectedMuse === 'object' &&
              (student.selectedMuse as any).img) ||
            '/default-avatar.png',
        }))}
        onConfirmReward={handleRewardConfirm}
        isAwarding={false}
      />

      <RewardConfirmationModal
        isOpen={isRewardConfirmationOpen}
        onClose={() => setIsRewardConfirmationOpen(false)}
        selectedStudents={pendingReward?.students || []}
        rewardAmount={pendingReward?.amount || 0}
        onConfirm={handleConfirmReward}
        isAwarding={isAwardingReward}
      />

      <SquadGameAccessModal
        squad={squad}
        isOpen={isGameAccessOpen}
        onClose={() => setIsGameAccessOpen(false)}
        teacherId={currentUser?.uid}
        teacherName={currentUser?.displayName || 'Teacher'}
      />

      <SquadBoardModeModal
        squad={squad}
        students={students}
        isOpen={isBoardModeOpen}
        onClose={() => setIsBoardModeOpen(false)}
      />

      {isTimerOpen && <DraggableTimer onClose={() => setIsTimerOpen(false)} />}
      {isMusicOpen && <DraggableMusic onClose={() => setIsMusicOpen(false)} />}

      <BonusActivitiesModal
        isOpen={isBonusActivitiesOpen}
        onClose={() => setIsBonusActivitiesOpen(false)}
      />

      {activity && (
        <ActivityModal
          activity={activity}
          isOpen={showInstructionsModal}
          onClose={() => setShowInstructionsModal(false)}
          size="large"
          // We intentionally hide the Start Activity button on this mission dashboard view
          onStart={undefined}
          levelId={levelId}
          curriculumId={curriculumId}
          showStartButton={false}
        />
      )}
    </div>
  );
};

export default MissionDashboard;
